import {
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonNav,
  IonNavLink,
  IonNote,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { Inbox } from "@models/inbox";
import DeckCreateMainNav from "@screens/deckCreate/main";
import ImportScreen, { setInitialImportTab } from "@screens/import";
import logEvent from "analytics";
import { History } from "history";
import L10n from "localization";
import { useCallback, useEffect, useRef } from "react";
import {
  PiCamera,
  PiFileArrowDown,
  PiFileCsv,
  PiFilePdf,
  PiMagnifyingGlass,
  PiNotePencil,
} from "react-icons/pi";

interface IProps {
  dismiss: () => Promise<void>;
  onAddToInbox: () => void;
  history: History;
}
export function GetStartedNav(props: IProps): JSX.Element {
  const { dismiss } = props;

  const handlePhoto = useCallback(async () => {
    await dismiss();
    props.onAddToInbox();
    logEvent("get_started_event", { step: "picked_option", option: "photo" });
  }, [dismiss, props.onAddToInbox]);

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const handlePDF = useCallback(
    async (file: File) => {
      await Inbox.addBlobToInbox(file);
      await dismiss();
      if (location.pathname !== "/inbox") {
        props.history.push("/inbox");
      }
      logEvent("inbox_event", { step: "item_added" });
      logEvent("get_started_event", { step: "picked_option", option: "pdf" });
    },
    [dismiss, props.history],
  );

  const handleSearch = useCallback(async () => {
    await dismiss();
    props.history.push("/search");
    logEvent("get_started_event", { step: "picked_option", option: "search" });
  }, [dismiss, props.history]);

  useEffect(() => {
    logEvent("get_started_event", { step: "showed_modal" });
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{L10n.localize((s) => s.getStarted.getStarted)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <IonNote>
          <p className="ion-margin-horizontal ion-text-wrap">
            {L10n.localize((s) => s.getStarted.description)}
          </p>
        </IonNote>

        <IonText color="medium">
          <h3 className="ion-margin-horizontal" style={{ fontSize: "1.1rem", marginBottom: 8 }}>
            {L10n.localize((s) => s.getStarted.makeContent)}
          </h3>
        </IonText>
        <IonList inset style={{ marginBottom: 16, marginTop: 0 }}>
          <IonNavLink
            routerDirection="forward"
            component={() => {
              logEvent("get_started_event", { step: "picked_option", option: "manual" });
              return <DeckCreateMainNav history={props.history} dismiss={dismiss} nav />;
            }}
          >
            <IonItem button>
              <div slot="start">
                <PiNotePencil size={24} style={{ color: "var(--ion-color-primary)" }} />
              </div>
              <IonLabel>
                <h2>{L10n.localize((s) => s.deck.new)}</h2>
                <p>{L10n.localize((s) => s.getStarted.manualDescription)}</p>
              </IonLabel>
            </IonItem>
          </IonNavLink>
        </IonList>

        <IonText color="medium">
          <h3 className="ion-margin-horizontal" style={{ fontSize: "1.1rem" }}>
            {L10n.localize((s) => s.getStarted.loadContent)}
          </h3>
        </IonText>
        <IonList inset style={{ marginBottom: 16, marginTop: 0 }}>
          <IonItem button>
            <div slot="start">
              <PiFilePdf size={24} style={{ color: "var(--ion-color-primary)" }} />
            </div>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                if (evt.target.files && evt.target.files.length > 0) {
                  const file = evt.target.files[0];
                  handlePDF(file);
                }
              }}
            />
            <IonLabel onClick={() => pdfInputRef.current?.click()}>
              <h2>{L10n.localize((s) => s.getStarted.pdf)}</h2>
              <p>{L10n.localize((s) => s.getStarted.pdfDescription)}</p>
            </IonLabel>
          </IonItem>
          <IonItem button>
            <div slot="start">
              <PiCamera size={24} style={{ color: "var(--ion-color-primary)" }} />
            </div>
            <IonLabel onClick={handlePhoto}>
              <h2>{L10n.localize((s) => s.getStarted.photo)}</h2>
              <p>{L10n.localize((s) => s.getStarted.photoDescription)}</p>
            </IonLabel>
          </IonItem>
          <IonNavLink
            routerDirection="forward"
            component={() => {
              setInitialImportTab("CSV");
              logEvent("get_started_event", { step: "picked_option", option: "spreadsheet" });
              return <ImportScreen dismiss={props.dismiss} history={props.history} nav />;
            }}
          >
            <IonItem button>
              <div slot="start">
                <PiFileCsv size={24} style={{ color: "var(--ion-color-primary)" }} />
              </div>
              <IonLabel>
                <h2>{L10n.localize((s) => s.getStarted.spreadsheet)}</h2>
                <p>{L10n.localize((s) => s.getStarted.spreadsheetDescription)}</p>
              </IonLabel>
            </IonItem>
          </IonNavLink>
          <IonNavLink
            routerDirection="forward"
            component={() => {
              setInitialImportTab("FML");
              logEvent("get_started_event", { step: "picked_option", option: "import" });
              return <ImportScreen dismiss={props.dismiss} history={props.history} nav />;
            }}
          >
            <IonItem button>
              <div slot="start">
                <PiFileArrowDown size={24} style={{ color: "var(--ion-color-primary)" }} />
              </div>
              <IonLabel>
                <h2>{L10n.localize((s) => s.getStarted.import)}</h2>
                <p>{L10n.localize((s) => s.getStarted.importDescription)}</p>
              </IonLabel>
            </IonItem>
          </IonNavLink>
        </IonList>

        <IonText color="medium">
          <h3 className="ion-margin-horizontal" style={{ fontSize: "1.1rem" }}>
            {L10n.localize((s) => s.getStarted.findContent)}
          </h3>
        </IonText>
        <IonList inset style={{ marginTop: 0 }}>
          <IonItem button>
            <div slot="start">
              <PiMagnifyingGlass size={24} style={{ color: "var(--ion-color-primary)" }} />
            </div>
            <IonLabel onClick={handleSearch}>
              <h2>{L10n.localize((s) => s.actions.search)}</h2>
              <p>{L10n.localize((s) => s.getStarted.searchDescription)}</p>
            </IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
    </IonPage>
  );
}

export default function GetStartedModal(props: IProps): JSX.Element {
  const navRoot = useCallback(() => {
    return (
      <GetStartedNav
        dismiss={props.dismiss}
        onAddToInbox={props.onAddToInbox}
        history={props.history}
      />
    );
  }, [props.dismiss, props.onAddToInbox, props.history]);
  return <IonNav root={navRoot} />;
}
